// src/componentes/Comisiones/SeccionComision.jsx
// Sección colapsable que se incrusta en AgregarDeuda para capturar la comisión.
//
// Modelo de plan de devengo: MANUAL FORZOSO con dos modos de distribución:
//   - "porcentaje" (default): el usuario define qué % del total se devenga en
//     cada hito. La suma debe ser 100%.
//   - "monto": el usuario define el monto absoluto en pesos por hito.
//     La suma debe ser el monto total de la comisión.
//
// El default se carga con dos hitos al 50/50 (Enganche, Mensualidad 1) — el
// usuario puede agregar, quitar y editar hitos libremente.
//
// Internamente se guarda como esquema: "custom" + hitosCustom: [...]
// (lo que mantiene compatibilidad con comisiones legacy y parseEsquemaComision).
//
// onChange emite:
//   { activa, comisionistaId, comisionistaNombre,
//     tipoCalculo, porcentaje, montoFijo,
//     esquema, mensualidadesN, hitosCustom,
//     distribucionPor }     // ← nuevo: "porcentaje" | "monto"

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaHandshake, FaChevronDown, FaChevronUp, FaPercent, FaDollarSign, FaPlus, FaTrash } from "react-icons/fa";
import { searchComisionistas } from "../../services/comisionistasService";
import { dialogService } from "../common/dialogService";
import notify from "../common/notify";
import "./Comisiones.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

// CV-5: detalle de comisión por hito, debe mostrar centavos
const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const roundCent = (n) => Math.round(n * 100) / 100;

// Triggers fijos (no son mensualidades)
const TRIGGER_FIJOS = [
  { value: "contrato.firmado", label: "Al firmar contrato" },
  { value: "enganche.cobrado", label: "Al cobrar enganche" },
];

const MIN_MENSUALIDADES_DROPDOWN = 24;  // default mínimo en el selector
const HARD_CAP_MENSUALIDADES = 360;     // hasta 30 años; safety net

// Genera la lista de triggers disponibles. El bound depende del plan actual:
// el selector siempre muestra al menos 24 meses, y se extiende cuando el
// plan ya usa más (ej. autoDesdeMensual genero 48 hitos → mostramos 50).
function buildTriggerOpciones(plan) {
  const usadas = plan
    .map((h) => {
      const m = /^mensualidad\.(\d+)$/.exec(h.trigger || "");
      return m ? Number(m[1]) : 0;
    })
    .filter((n) => n > 0);
  const maxUsada = usadas.length ? Math.max(...usadas) : 0;
  const tope = Math.min(
    HARD_CAP_MENSUALIDADES,
    Math.max(MIN_MENSUALIDADES_DROPDOWN, maxUsada + 6)
  );
  const mens = Array.from({ length: tope }, (_, i) => ({
    value: `mensualidad.${i + 1}`,
    label: `Mensualidad ${i + 1}`,
  }));
  return [...TRIGGER_FIJOS, ...mens];
}

// Default: 50/50 al enganche + mensualidad 1
const HITOS_DEFAULT_50_50 = () => ([
  { etiqueta: "Enganche",     trigger: "enganche.cobrado", pct: 50, monto: 0 },
  { etiqueta: "Mensualidad 1", trigger: "mensualidad.1",   pct: 50, monto: 0 },
]);

// Convierte el modelo interno (con pct y monto) al shape que espera el
// helper / firestore (sólo `monto`).
function planAHitosCustom(plan, distribucionPor, totalAbs) {
  if (distribucionPor === "porcentaje") {
    // Tomar pct de cada hito, calcular monto = pct * totalAbs / 100
    // El último hito absorbe el redondeo para que la suma sea exacta.
    let asignado = 0;
    return plan.map((h, i) => {
      const isLast = i === plan.length - 1;
      const pct = Number(h.pct) || 0;
      let monto;
      if (isLast) {
        monto = roundCent(totalAbs - asignado);
      } else {
        monto = roundCent((totalAbs * pct) / 100);
        asignado = roundCent(asignado + monto);
      }
      return {
        etiqueta: h.etiqueta || `Hito ${i + 1}`,
        trigger: h.trigger || "enganche.cobrado",
        monto,
      };
    });
  }
  // Modo monto: copiar montos absolutos directos
  return plan.map((h, i) => ({
    etiqueta: h.etiqueta || `Hito ${i + 1}`,
    trigger: h.trigger || "enganche.cobrado",
    monto: roundCent(Number(h.monto) || 0),
  }));
}

// ── Componente ───────────────────────────────────────────────────────────────

export default function SeccionComision({
  montoDeuda = 0,
  engancheDeuda = 0,
  mensualidadDeuda = 0,
  onChange,
  initialData = null,
}) {
  const init = initialData || {};
  const initActiva = !!init.activa;

  const [abierta, setAbierta] = useState(initActiva);
  const [activa,  setActiva]  = useState(initActiva);

  // Comisionista
  const [comisionistaId, setComisionistaId]       = useState(init.comisionistaId || "");
  const [comisionistaNombre, setComisionistaNombre] = useState(init.comisionistaNombre || "");
  const [searchTerm, setSearchTerm] = useState(init.comisionistaNombre || "");
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Tipo de cálculo del MONTO TOTAL (porcentaje del precio venta vs monto fijo)
  const [tipoCalculo, setTipoCalculo] = useState(init.tipoCalculo || "porcentaje");
  const [porcentaje, setPorcentaje]   = useState(
    init.porcentaje != null ? String((Number(init.porcentaje) * 100).toFixed(2)) : "3"
  );
  const [montoFijo, setMontoFijo] = useState(
    init.montoFijo != null ? String(init.montoFijo) : ""
  );

  // ── Plan de devengo manual ─────────────────────────────────────────────
  // distribucionPor: "porcentaje" → suma de pcts = 100%; "monto" → suma de montos = totalAbs
  const [distribucionPor, setDistribucionPor] = useState(
    init.distribucionPor || "porcentaje"
  );
  const [plan, setPlan] = useState(() => {
    // Si initialData trae hitosCustom, los convertimos al modelo interno.
    if (Array.isArray(init.hitosCustom) && init.hitosCustom.length > 0) {
      return init.hitosCustom.map((h) => ({
        etiqueta: h.etiqueta || "",
        trigger: h.trigger || "enganche.cobrado",
        pct: 0,    // se recalcula si se entra en modo porcentaje
        monto: Number(h.monto) || 0,
      }));
    }
    return HITOS_DEFAULT_50_50();
  });

  // ── Total absoluto de la comisión (depende de tipoCalculo) ─────────────
  const totalAbs = useMemo(() => {
    if (tipoCalculo === "porcentaje") {
      const pct = Number(porcentaje) / 100;
      const base = Number(montoDeuda);
      if (!pct || !base) return 0;
      return roundCent(base * pct);
    }
    return roundCent(Number(montoFijo) || 0);
  }, [tipoCalculo, porcentaje, montoFijo, montoDeuda]);

  // ── Buscar comisionistas con debounce ──────────────────────────────────
  useEffect(() => {
    if (!activa || !searchTerm || comisionistaId) return;
    let alive = true;
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await searchComisionistas(searchTerm, 8);
        if (alive) setSugerencias(res);
      } finally {
        if (alive) setBuscando(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [searchTerm, activa, comisionistaId]);

  // ── Triggers disponibles (dinamico segun cuanto crece el plan) ──────────
  const TRIGGER_OPCIONES = useMemo(() => buildTriggerOpciones(plan), [plan]);

  // ── Suma actual (porcentajes o montos según el modo) ───────────────────
  const sumaActual = useMemo(() => {
    if (distribucionPor === "porcentaje") {
      return roundCent(plan.reduce((acc, h) => acc + (Number(h.pct) || 0), 0));
    }
    return roundCent(plan.reduce((acc, h) => acc + (Number(h.monto) || 0), 0));
  }, [plan, distribucionPor]);

  const sumaObjetivo = distribucionPor === "porcentaje" ? 100 : totalAbs;
  const sumaTolerancia = distribucionPor === "porcentaje" ? 0.1 : 0.5;
  const sumaValida = Math.abs(sumaActual - sumaObjetivo) <= sumaTolerancia;

  // ── Detección de hitos que exceden el flujo de efectivo de su trigger ──
  // (ej. comisión $2,333 al hito-mensualidad cuando la mensualidad cliente
  //  es $1,000 → la empresa pone $1,333 de su bolsa ese mes.)
  const hitosExcedidos = useMemo(() => {
    const out = [];
    plan.forEach((h, i) => {
      const montoH = distribucionPor === "porcentaje"
        ? roundCent((totalAbs * (Number(h.pct) || 0)) / 100)
        : roundCent(Number(h.monto) || 0);
      let flujo = 0;
      if (h.trigger === "enganche.cobrado") flujo = Number(engancheDeuda) || 0;
      else if (/^mensualidad\.\d+$/.test(h.trigger)) flujo = Number(mensualidadDeuda) || 0;
      if (flujo > 0 && montoH > flujo) out.push({ i, montoH, flujo, label: h.etiqueta });
    });
    return out;
  }, [plan, distribucionPor, totalAbs, engancheDeuda, mensualidadDeuda]);

  // ── Emitir al padre ────────────────────────────────────────────────────
  useEffect(() => {
    if (!onChange) return;
    const hitosCustom = planAHitosCustom(plan, distribucionPor, totalAbs);
    onChange({
      activa,
      comisionistaId,
      comisionistaNombre,
      tipoCalculo,
      porcentaje: Number(porcentaje) / 100,
      montoFijo: Number(montoFijo) || 0,
      esquema: "custom",       // siempre custom — el plan manual es la única vía
      mensualidadesN: 0,        // no aplica al modelo manual
      hitosCustom,
      distribucionPor,
      planValido: sumaValida && totalAbs > 0,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa, comisionistaId, tipoCalculo, porcentaje, montoFijo, plan, distribucionPor, totalAbs, sumaValida]);

  // ── Acciones ───────────────────────────────────────────────────────────
  const seleccionarComisionista = useCallback((c) => {
    setComisionistaId(c.id);
    setComisionistaNombre(`${c.nombre || ""} ${c.apellidos || ""}`.trim());
    setSearchTerm(`${c.nombre || ""} ${c.apellidos || ""}`.trim());
    setSugerencias([]);
    if (c.tarifaPorDefecto) {
      setPorcentaje(String((c.tarifaPorDefecto * 100).toFixed(2)));
    }
  }, []);

  const limpiarComisionista = () => {
    setComisionistaId(""); setComisionistaNombre("");
    setSearchTerm(""); setSugerencias([]);
  };

  // Cambiar modo de distribución: si pasa a "porcentaje", recalcular pcts
  // a partir de los montos. Si pasa a "monto", recalcular montos desde pcts.
  const cambiarDistribucion = (nuevo) => {
    setPlan((prev) => {
      if (nuevo === "porcentaje" && totalAbs > 0) {
        return prev.map((h) => ({
          ...h,
          pct: roundCent(((Number(h.monto) || 0) / totalAbs) * 100),
        }));
      }
      if (nuevo === "monto" && totalAbs > 0) {
        let asignado = 0;
        return prev.map((h, i) => {
          const isLast = i === prev.length - 1;
          let monto;
          if (isLast) monto = roundCent(totalAbs - asignado);
          else {
            monto = roundCent((totalAbs * (Number(h.pct) || 0)) / 100);
            asignado = roundCent(asignado + monto);
          }
          return { ...h, monto };
        });
      }
      return prev;
    });
    setDistribucionPor(nuevo);
  };

  const updateHito = (i, field, value) => {
    setPlan((prev) => {
      const next = [...prev];
      const prevHito = next[i];
      next[i] = { ...prevHito, [field]: value };

      // Si cambias el trigger, autoajustar la etiqueta cuando sigue
      // siendo la genérica ("Hito N" o vacía). Si el user ya editó
      // la etiqueta, respetar su valor.
      if (field === "trigger") {
        const etiquetaActual = String(prevHito.etiqueta || "").trim();
        const esGenerica =
          !etiquetaActual ||
          /^hito\s+\d+$/i.test(etiquetaActual) ||
          etiquetaActual === `Hito ${i + 1}`;
        if (esGenerica) {
          const opt = TRIGGER_OPCIONES.find((t) => t.value === value);
          if (opt) {
            // Mensualidad N → "Mes N". Otros → label tal cual.
            const m = /^mensualidad\.(\d+)$/.exec(value);
            next[i].etiqueta = m ? `Mensualidad ${m[1]}` : opt.label;
          }
        }
      }

      return next;
    });
  };

  const addHito = () => {
    setPlan((prev) => {
      // Calcular el siguiente número de mensualidad disponible
      // basándose en los triggers ya usados.
      const mensualidadesUsadas = prev
        .map((h) => {
          const m = /^mensualidad\.(\d+)$/.exec(h.trigger || "");
          return m ? Number(m[1]) : 0;
        })
        .filter((n) => n > 0);
      const proximoN = mensualidadesUsadas.length > 0
        ? Math.max(...mensualidadesUsadas) + 1
        : 1;
      return [
        ...prev,
        {
          etiqueta: `Mensualidad ${proximoN}`,
          trigger: `mensualidad.${proximoN}`,
          pct: 0,
          monto: 0,
        },
      ];
    });
  };

  const removeHito = (i) => {
    setPlan((prev) => prev.filter((_, idx) => idx !== i));
  };

  const aplicarPreset5050 = () => {
    setPlan(HITOS_DEFAULT_50_50());
    if (distribucionPor === "monto" && totalAbs > 0) {
      cambiarDistribucion("porcentaje");
    }
  };

  const distribuirIgual = () => {
    setPlan((prev) => {
      const n = prev.length;
      if (n === 0) return prev;
      if (distribucionPor === "porcentaje") {
        const base = roundCent(100 / n);
        let asignado = 0;
        return prev.map((h, i) => {
          const isLast = i === n - 1;
          const pct = isLast ? roundCent(100 - asignado) : base;
          asignado = roundCent(asignado + base);
          return { ...h, pct };
        });
      }
      if (totalAbs <= 0) return prev;
      const base = roundCent(totalAbs / n);
      let asignado = 0;
      return prev.map((h, i) => {
        const isLast = i === n - 1;
        const monto = isLast ? roundCent(totalAbs - asignado) : base;
        asignado = roundCent(asignado + base);
        return { ...h, monto };
      });
    });
  };

  // ── Anclar enganche + repartir resto en N mensualidades ─────────────────
  // Pide al usuario:
  //   1. % del enganche (puede ser 0–100)
  //   2. # de mensualidades en las que distribuir el resto
  // Genera: [Enganche(pct=X), Mensualidad 1..N(pct=(100-X)/N)]
  // Cambia el modo a "porcentaje" para que sea legible.
  const anclarEngancheYRepartir = async () => {
    const pctStr = await dialogService.prompt(
      "¿Qué porcentaje del total se devenga al cobrar el enganche?",
      "30"
    );
    if (pctStr === null) return;
    const pctEnganche = Number(pctStr);
    if (!Number.isFinite(pctEnganche) || pctEnganche < 0 || pctEnganche > 100) {
      return;
    }
    const nStr = await dialogService.prompt(
      `Restante: ${(100 - pctEnganche).toFixed(2)}%. ¿En cuántas mensualidades repartirlo?`,
      "6"
    );
    if (nStr === null) return;
    const n = Math.floor(Number(nStr));
    if (!Number.isInteger(n) || n < 1) return;

    const pctRestante = 100 - pctEnganche;
    const base = roundCent(pctRestante / n);
    let asignado = 0;
    const mensualidades = Array.from({ length: n }, (_, i) => {
      const isLast = i === n - 1;
      const pct = isLast ? roundCent(pctRestante - asignado) : base;
      asignado = roundCent(asignado + base);
      return {
        etiqueta: `Mensualidad ${i + 1}`,
        trigger: `mensualidad.${i + 1}`,
        pct,
        monto: 0,
      };
    });

    setPlan([
      { etiqueta: "Enganche", trigger: "enganche.cobrado", pct: roundCent(pctEnganche), monto: 0 },
      ...mensualidades,
    ]);
    setDistribucionPor("porcentaje");
  };

  // ── Auto-generar desde % del flujo real del cliente ─────────────────────
  // El usuario define dos porcentajes del FLUJO REAL DEL CLIENTE:
  //   1. % del enganche del cliente que va a comisión
  //      (ej. enganche $1,000 × 10% → $100 al hito de enganche)
  //   2. % de cada mensualidad del cliente que va a comisión
  //      (ej. mens $2,000 × 5%  → $100 por hito mensual)
  //
  // El sistema calcula cuantas mensualidades son necesarias para cubrir el
  // resto de la comisión: nMens = ceil((total - montoEng) / porMensualidad).
  // El ultimo hito puede ser menor (absorbe el residuo del redondeo).
  // Cambia a modo "monto" porque los importes son fijos.
  const autoDesdeMensual = async () => {
    if (!mensualidadDeuda || mensualidadDeuda <= 0) {
      return dialogService.confirm(
        "Para usar esta opción necesitas haber capturado la mensualidad de la deuda primero."
      );
    }
    if (!totalAbs || totalAbs <= 0) {
      return dialogService.confirm(
        "Captura primero la tarifa o el monto fijo de la comisión."
      );
    }

    const tieneEnganche = Number(engancheDeuda) > 0;

    // ── Input 1: % del enganche del cliente ─────────────────────────────
    let pctEng = 0;
    if (tieneEnganche) {
      const pctEngStr = await dialogService.prompt(
        `Enganche del cliente: ${formatMXN(engancheDeuda)}.\n\n` +
        `¿Qué % del enganche del cliente va a comisión?\n` +
        `(Ej. 10 → se devenga ${formatMXN(engancheDeuda * 0.1)} al cobrar el enganche)\n\n` +
        `0 = sin hito de enganche`,
        "10"
      );
      if (pctEngStr === null) return;
      pctEng = Number(pctEngStr);
      if (!Number.isFinite(pctEng) || pctEng < 0 || pctEng > 100) return;
    }

    // ── Input 2: % de cada mensualidad del cliente ──────────────────────
    const pctMensStr = await dialogService.prompt(
      `Mensualidad del cliente: ${formatMXN(mensualidadDeuda)}.\n\n` +
      `¿Qué % de cada mensualidad del cliente va a comisión?\n` +
      `(Ej. 5 → cada mensualidad aporta ${formatMXN(mensualidadDeuda * 0.05)} a la comisión)`,
      "5"
    );
    if (pctMensStr === null) return;
    const pctMens = Number(pctMensStr);
    if (!Number.isFinite(pctMens) || pctMens <= 0 || pctMens > 100) {
      return notify.error("El % de la mensualidad debe ser mayor a 0 y hasta 100.");
    }

    // ── Calculos ────────────────────────────────────────────────────────
    const montoEnganche = roundCent((Number(engancheDeuda) || 0) * (pctEng / 100));
    const porMensualidad = roundCent(mensualidadDeuda * (pctMens / 100));

    if (porMensualidad <= 0) {
      return notify.error("El monto por mensualidad da cero. Sube el porcentaje.");
    }

    // Caso 1: el enganche ya cubre o excede el total → 1 solo hito de enganche
    if (montoEnganche >= totalAbs) {
      const plan = [{
        etiqueta: "Enganche",
        trigger: "enganche.cobrado",
        pct: 0,
        monto: roundCent(totalAbs),
      }];
      setPlan(plan);
      setDistribucionPor("monto");
      notify.info(
        `Solo se requiere 1 hito: el enganche del cliente ya cubre la comisión completa ` +
        `(${formatMXN(totalAbs)}).`
      );
      return;
    }

    const restante = roundCent(totalAbs - montoEnganche);
    const nMens = Math.ceil(restante / porMensualidad);

    if (nMens > HARD_CAP_MENSUALIDADES) {
      return notify.error(
        `Necesitarías ${nMens} mensualidades (más que el tope de ${HARD_CAP_MENSUALIDADES}). ` +
        `Sube el % por mensualidad o el % del enganche.`
      );
    }

    if (nMens > 36) {
      const ok = await dialogService.confirm(
        `Se necesitarán ${nMens} mensualidades (${formatMXN(porMensualidad)} c/u) para cubrir ` +
        `el restante de ${formatMXN(restante)}.\n\n¿Continuar?`
      );
      if (!ok) return;
    }

    // ── Generar el plan ─────────────────────────────────────────────────
    // Las primeras (nMens-1) son del valor porMensualidad; la última absorbe el residuo.
    let asignado = 0;
    const mensualidades = Array.from({ length: nMens }, (_, i) => {
      const isLast = i === nMens - 1;
      const monto = isLast ? roundCent(restante - asignado) : porMensualidad;
      asignado = roundCent(asignado + porMensualidad);
      return {
        etiqueta: `Mensualidad ${i + 1}`,
        trigger: `mensualidad.${i + 1}`,
        pct: 0,
        monto,
      };
    });

    const plan = [
      ...(montoEnganche > 0
        ? [{ etiqueta: "Enganche", trigger: "enganche.cobrado", pct: 0, monto: montoEnganche }]
        : []),
      ...mensualidades,
    ];

    setPlan(plan);
    setDistribucionPor("monto");

    const ultimoMonto = mensualidades[mensualidades.length - 1]?.monto ?? 0;
    const sufijoUltimo = ultimoMonto !== porMensualidad
      ? ` (el último hito es ${formatMXN(ultimoMonto)} por ajuste de redondeo)`
      : "";
    notify.info(
      `Plan generado: ${plan.length} hito${plan.length !== 1 ? "s" : ""}. ` +
      (montoEnganche > 0 ? `Enganche aporta ${formatMXN(montoEnganche)} (${pctEng}%). ` : "") +
      `Cada mensualidad aporta ${formatMXN(porMensualidad)} (${pctMens}%)` + sufijoUltimo + `.`
    );
  };

  return (
    <div className="com-seccion">
      {/* ─── Header / toggle ──────────────────────────────────────────── */}
      <div className="com-seccion-head" onClick={() => setAbierta((v) => !v)}>
        <FaHandshake size={18} />
        <h4>Comisión por venta</h4>
        <span style={{ marginLeft: "auto", color: "#3b82f6" }}>
          {abierta ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      {abierta && (
        <div className="com-seccion-body">

          {/* ─── Activar ──────────────────────────────────────────────── */}
          <label style={{ display:"flex", alignItems:"center", gap:10, fontSize:14, fontWeight:600, color:"#1e40af", cursor:"pointer", userSelect:"none" }}>
            <input
              type="checkbox"
              checked={activa}
              onChange={(e) => setActiva(e.target.checked)}
              style={{ width:16, height:16, cursor:"pointer", flexShrink:0, accentColor:"#1e40af" }}
            />
            <span>Asociar comisión a esta venta</span>
          </label>

          {activa && (
            <>
              {/* ─── Comisionista ───────────────────────────────────── */}
              <div className="com-campo">
                <label>Comisionista *</label>
                {comisionistaId ? (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#fff", border:"1px solid #93c5fd", borderRadius:8, padding:"8px 12px", fontSize:14 }}>
                    <span style={{ fontWeight:600 }}>{comisionistaNombre}</span>
                    <button type="button" onClick={limpiarComisionista} style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:16 }}>×</button>
                  </div>
                ) : (
                  <>
                    <input
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setComisionistaId(""); }}
                      placeholder="Buscar comisionista por nombre…"
                    />
                    {buscando && <span style={{ fontSize:12, color:"#94a3b8" }}>Buscando…</span>}
                    {sugerencias.length > 0 && (
                      <div className="com-suggestions">
                        {sugerencias.map((c) => (
                          <button key={c.id} type="button" className="com-suggestion-item" onClick={() => seleccionarComisionista(c)}>
                            {c.nombre} {c.apellidos}
                            {c.tarifaPorDefecto && <span style={{ color:"#9ca3af", marginLeft:8 }}>{(c.tarifaPorDefecto*100).toFixed(1)}%</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {searchTerm && sugerencias.length === 0 && !buscando && (
                      <span style={{ fontSize:12, color:"#94a3b8" }}>
                        No encontrado.{" "}
                        <a href="/app/comisionistas/nuevo" target="_blank" rel="noreferrer" style={{ color:"#3b82f6" }}>Crear comisionista →</a>
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* ─── Toggle Porcentaje / Monto fijo (cálculo del TOTAL) ── */}
              <div className="com-campo">
                <label>Cálculo de la comisión total</label>
                <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1px solid #bfdbfe" }}>
                  {[
                    { val:"porcentaje", label:"% del precio venta", Icon: FaPercent },
                    { val:"monto_fijo", label:"$ Monto fijo pactado", Icon: FaDollarSign },
                  ].map(({ val, label, Icon }) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setTipoCalculo(val)}
                      style={{
                        flex:1,
                        padding:"8px 12px",
                        border:"none",
                        cursor:"pointer",
                        fontSize:13,
                        fontWeight:600,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"center",
                        gap:6,
                        background: tipoCalculo === val ? "#1e40af" : "#fff",
                        color:      tipoCalculo === val ? "#fff"    : "#64748b",
                        transition:"background 0.15s",
                      }}
                    >
                      <Icon size={11} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ─── Input según modo ───────────────────────────────── */}
              {tipoCalculo === "porcentaje" ? (
                <div className="com-campo" style={{ maxWidth: 200 }}>
                  <label>Tarifa (%)</label>
                  <input
                    type="number"
                    min="0.01" max="100" step="0.01"
                    value={porcentaje}
                    onChange={(e) => setPorcentaje(e.target.value)}
                    placeholder="3"
                  />
                  {montoDeuda > 0 && Number(porcentaje) > 0 && (
                    <span style={{ fontSize:12, color:"#64748b" }}>
                      = {formatMXN(totalAbs)}
                    </span>
                  )}
                </div>
              ) : (
                <div className="com-campo" style={{ maxWidth: 220 }}>
                  <label>Monto pactado (MXN)</label>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={montoFijo}
                    onChange={(e) => setMontoFijo(e.target.value)}
                    placeholder="Ej. 50000"
                  />
                  {montoDeuda > 0 && totalAbs > 0 && (
                    <span style={{ fontSize:12, color:"#64748b" }}>
                      Equivale al {((totalAbs/montoDeuda)*100).toFixed(2)}% del precio venta
                    </span>
                  )}
                </div>
              )}

              {/* ─── Plan de devengo manual ────────────────────────── */}
              <div className="com-plan-manual">
                <div className="com-plan-head">
                  <div>
                    <h5>Plan de devengo (manual)</h5>
                    <p>Define cuánto se devenga en cada hito. Es obligatorio que la suma cuadre con el total.</p>
                  </div>
                  <div className="com-plan-actions">
                    <button type="button" className="com-plan-btn-link" onClick={aplicarPreset5050} title="Preset: 50% enganche + 50% mensualidad 1">
                      50/50
                    </button>
                    <button type="button" className="com-plan-btn-link" onClick={distribuirIgual} title="Distribuir el total en partes iguales entre los hitos actuales">
                      Distribuir igual
                    </button>
                    <button
                      type="button"
                      className="com-plan-btn-link"
                      onClick={anclarEngancheYRepartir}
                      title="Capturas el % del enganche y cuántas mensualidades; el sistema reparte el resto"
                    >
                      Anclar enganche…
                    </button>
                    <button
                      type="button"
                      className="com-plan-btn-link"
                      onClick={autoDesdeMensual}
                      title="Capturas % del enganche del cliente y % de cada mensualidad del cliente; el sistema calcula cuántos meses se necesitan para cubrir la comisión."
                      disabled={!mensualidadDeuda || mensualidadDeuda <= 0}
                    >
                      Auto por % del cliente…
                    </button>
                  </div>
                </div>

                {/* Selector porcentaje / monto */}
                <div className="com-plan-dist-toggle">
                  {[
                    { val:"porcentaje", label:"Distribuir por %" },
                    { val:"monto",      label:"Distribuir por $" },
                  ].map(({ val, label }) => (
                    <button
                      key={val}
                      type="button"
                      className={`com-plan-dist-opt ${distribucionPor === val ? "active" : ""}`}
                      onClick={() => cambiarDistribucion(val)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Tabla de hitos */}
                <div className="com-plan-table-wrap">
                  <table className="com-plan-table">
                    <thead>
                      <tr>
                        <th>Etiqueta</th>
                        <th>Cuándo se devenga</th>
                        <th style={{ textAlign: "right", width: 130 }}>
                          {distribucionPor === "porcentaje" ? "% del total" : "Monto"}
                        </th>
                        <th style={{ textAlign: "right", width: 110 }}>Equivalente</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.map((h, i) => {
                        const equivalente =
                          distribucionPor === "porcentaje"
                            ? roundCent((totalAbs * (Number(h.pct) || 0)) / 100)
                            : totalAbs > 0
                            ? `${(((Number(h.monto) || 0) / totalAbs) * 100).toFixed(1)}%`
                            : "—";

                        // Monto absoluto del hito (sea modo % o $)
                        const montoHito = distribucionPor === "porcentaje"
                          ? roundCent((totalAbs * (Number(h.pct) || 0)) / 100)
                          : roundCent(Number(h.monto) || 0);

                        // Flujo de efectivo que dispara este hito según el trigger
                        let flujoTrigger = 0;
                        let labelFlujo = "";
                        if (h.trigger === "enganche.cobrado") {
                          flujoTrigger = Number(engancheDeuda) || 0;
                          labelFlujo = "enganche";
                        } else if (/^mensualidad\.\d+$/.test(h.trigger)) {
                          flujoTrigger = Number(mensualidadDeuda) || 0;
                          labelFlujo = "mensualidad";
                        }
                        const excedeFlujo =
                          flujoTrigger > 0 && montoHito > flujoTrigger;

                        return (
                          <tr key={i}>
                            <td>
                              <input
                                type="text"
                                value={h.etiqueta}
                                onChange={(e) => updateHito(i, "etiqueta", e.target.value)}
                                placeholder={`Hito ${i + 1}`}
                              />
                            </td>
                            <td>
                              <select
                                value={h.trigger}
                                onChange={(e) => updateHito(i, "trigger", e.target.value)}
                              >
                                {TRIGGER_OPCIONES.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {distribucionPor === "porcentaje" ? (
                                <div className="com-plan-input-suffix">
                                  <input
                                    type="number"
                                    min="0" max="100" step="0.01"
                                    value={h.pct}
                                    onChange={(e) => updateHito(i, "pct", Number(e.target.value))}
                                  />
                                  <span>%</span>
                                </div>
                              ) : (
                                <div className="com-plan-input-suffix">
                                  <span style={{ paddingLeft: 0 }}>$</span>
                                  <input
                                    type="number"
                                    min="0" step="any"
                                    value={h.monto}
                                    onChange={(e) => updateHito(i, "monto", Number(e.target.value))}
                                  />
                                </div>
                              )}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                color: excedeFlujo ? "#b45309" : "#64748b",
                                fontSize: 12,
                              }}
                              title={
                                excedeFlujo
                                  ? `El monto del hito (${formatMXN(montoHito)}) es mayor que el ${labelFlujo} (${formatMXN(flujoTrigger)}). La empresa deberá pagar comisión sin haber recibido lo suficiente del cliente ese día.`
                                  : ""
                              }
                            >
                              {distribucionPor === "porcentaje" ? formatMXN(equivalente) : equivalente}
                              {excedeFlujo && (
                                <span style={{ marginLeft: 4 }} role="img" aria-label="warning">
                                  ⚠
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <button
                                type="button"
                                className="com-plan-btn-remove"
                                onClick={() => removeHito(i)}
                                disabled={plan.length === 1}
                                title="Quitar hito"
                              >
                                <FaTrash size={11} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <button type="button" className="com-plan-btn-add" onClick={addHito}>
                  <FaPlus size={11} /> Agregar hito
                </button>

                {/* Validación de suma */}
                <div className={`com-plan-suma ${sumaValida ? "ok" : "bad"}`}>
                  <span className="com-plan-suma-label">Suma actual:</span>
                  <span className="com-plan-suma-actual">
                    {distribucionPor === "porcentaje"
                      ? `${sumaActual.toFixed(2)}%`
                      : formatMXN(sumaActual)}
                  </span>
                  <span className="com-plan-suma-sep">/</span>
                  <span className="com-plan-suma-objetivo">
                    {distribucionPor === "porcentaje"
                      ? "100%"
                      : formatMXN(sumaObjetivo)}
                  </span>
                  <span className="com-plan-suma-badge">
                    {sumaValida ? "✓ Cuadra" : "✗ No cuadra"}
                  </span>
                </div>

                {/* Aviso global de hitos que exceden el flujo de su trigger */}
                {hitosExcedidos.length > 0 && (
                  <div className="com-plan-warning">
                    <strong>⚠ Atención al flujo de efectivo</strong>
                    <span>
                      {hitosExcedidos.length === 1
                        ? "1 hito tiene"
                        : `${hitosExcedidos.length} hitos tienen`}{" "}
                      un monto mayor que el ingreso del cliente que los dispara.
                      La empresa pagará comisión sin haber recibido lo suficiente
                      ese mes. Considera bajar el porcentaje o repartir en más hitos.
                    </span>
                  </div>
                )}
              </div>

              {/* ─── Preview / resumen ──────────────────────────────── */}
              {totalAbs > 0 && (
                <div className="com-preview-monto">
                  <div>
                    <div className="com-preview-label">Comisión total</div>
                    <div className="com-preview-sub">
                      {tipoCalculo === "porcentaje"
                        ? `${Number(porcentaje).toFixed(2)}% de ${formatMXN(montoDeuda)}`
                        : "Monto fijo pactado"}
                      {" · "}
                      {plan.length} hito{plan.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="com-preview-value">{formatMXN(totalAbs)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
